import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, Users, Clock, Shuffle } from 'lucide-react';
import { OnlineIndicator } from './OnlineIndicator';
import { getImageUrl } from '../utils/imageUtils';

const PROFILE_TYPE_LABELS = {
  homme: 'Homme',
  femme: 'Femme',
  couple: 'Couple'
};

export const DiscoverySidebar = () => {
  const { api, user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('zapping');

  useEffect(() => {
    fetchProfiles();
  }, [activeTab]);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      let endpoint = '/search';
      let params = new URLSearchParams();
      
      if (activeTab === 'zapping') {
        params.append('random', 'true');
        params.append('limit', '6');
      } else if (activeTab === 'nouveaux') {
        params.append('sort', 'newest');
        params.append('limit', '6');
      } else if (activeTab === 'online') {
        params.append('online', 'true');
        params.append('limit', '6');
      }
      
      const response = await api.get(`${endpoint}?${params.toString()}`);
      // Handle both array and object response format
      const data = response.data;
      const profilesList = Array.isArray(data) ? data : (data.profiles || []);
      // Filter out current user and shuffle for zapping
      let filteredProfiles = profilesList.filter(p => p.user_id !== user?.id);
      
      if (activeTab === 'zapping') {
        filteredProfiles = filteredProfiles.sort(() => Math.random() - 0.5);
      }
      
      setProfiles(filteredProfiles.slice(0, 6));
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const shuffleProfiles = () => {
    if (activeTab === 'zapping') {
      fetchProfiles();
    }
  };

  const tabs = [
    { id: 'zapping', label: 'Zapping', icon: Sparkles },
    { id: 'online', label: 'En ligne', icon: Users },
    { id: 'nouveaux', label: 'Nouveaux', icon: Clock }
  ];

  return (
    <div className="w-64 flex-shrink-0" data-testid="discovery-sidebar">
      <div className="sticky top-24">
        <div className="bg-dark-paper border border-white/10 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="p-3 border-b border-white/10 bg-gradient-to-r from-gold/10 to-transparent">
            <div className="flex items-center justify-between">
              <h3 className="text-gold font-semibold text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Découverte
              </h3>
              {activeTab === 'zapping' && (
                <button 
                  onClick={shuffleProfiles}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  title="Mélanger"
                >
                  <Shuffle className="w-4 h-4 text-gray-400 hover:text-gold" />
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2 px-1 text-xs font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'text-gold border-b-2 border-gold bg-gold/5' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Profiles Grid */}
          <div className="p-2">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-5 h-5 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
              </div>
            ) : profiles.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-gray-500 text-xs">Aucun profil trouvé</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {profiles.map((profile) => (
                  <Link
                    key={profile.user_id}
                    to={`/member/${profile.user_id}`}
                    className="group relative block"
                  >
                    {/* Profile Image */}
                    <div className="relative aspect-square rounded-lg overflow-hidden border border-white/10 group-hover:border-gold/50 transition-colors">
                      {profile.profile_picture ? (
                        <img
                          src={getImageUrl(profile.profile_picture)}
                          alt={profile.pseudo}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-dark-subtle flex items-center justify-center">
                          <span className="text-gold text-lg font-serif">
                            {profile.pseudo?.[0]?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      
                      {/* Overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      
                      {/* Online indicator */}
                      {profile.is_online && (
                        <div className="absolute top-1.5 right-1.5">
                          <OnlineIndicator isOnline={true} size="sm" />
                        </div>
                      )}
                      
                      {/* Verified badge */}
                      {profile.is_verified_by_team && (
                        <div className="absolute top-1.5 left-1.5">
                          <div className="w-4 h-4 rounded-full bg-gold flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-dark" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                      
                      {/* Profile info at bottom */}
                      <div className="absolute bottom-0 left-0 right-0 p-1.5">
                        <p className="text-white text-xs font-medium truncate">
                          {profile.pseudo}
                        </p>
                        <p className="text-gray-400 text-[10px] flex items-center gap-1">
                          <span className={`${
                            profile.profile_type === 'femme' ? 'text-pink-400' :
                            profile.profile_type === 'homme' ? 'text-blue-400' :
                            'text-purple-400'
                          }`}>
                            {profile.profile_type === 'femme' ? '♀' : 
                             profile.profile_type === 'homme' ? '♂' : '♂♀'}
                          </span>
                          {PROFILE_TYPE_LABELS[profile.profile_type] || profile.profile_type}
                          {profile.age && <span>• {profile.age}a</span>}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* View all link */}
          <div className="p-2 border-t border-white/10">
            <Link 
              to="/search" 
              className="block w-full text-center py-2 text-xs text-gold hover:text-gold-light transition-colors"
            >
              Voir tous les profils →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoverySidebar;
